import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

const DropdownContainer = styled.div`
  position: relative;
  margin: ${props => props.margin || '0px'};
  width: ${(props) => props.width || "300px"};
  max-width: 90%;
`;

const DropdownInput = styled.input`
  width: 100%;
  padding: 17px 40px 5px 10px;
  padding-right: ${(props) => (props.searchenabled ? "65px" : "40px")};
  color: #000000de;
  font-weight: 400;
  letter-spacing: 0.00937em;
  border: 2px solid #0000003d;
  border-radius: 4px;
  outline: none;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
  cursor: pointer;

  &:focus {
    border-color: #192d6e;
  }

  &:focus ~ label,
  &:not(:placeholder-shown) ~ label {
    top: 10px;
    left: 10px;
    font-size: 12px;
    color: #192d6e;
  }
`;

const FloatingLabel = styled.label`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: #0009;
  background: white;
  padding: 0;
  transition: all 0.3s ease;
  pointer-events: none;
`;

const DownArrowIcon = styled.svg`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 30px;
  height: 30px;
  stroke: #999;
  cursor: pointer;
`;

const SearchIcon = styled.img`
  position: absolute;
  right: 40px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  height: 20px;
  opacity: .9;
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  z-index: 1;
`
const OptionsList = styled.ul`
  position: absolute;
  // ${props => props.listplacement === 'bottom' ? 'top: 100%;' : 'bottom: 100%;'}
  z-index: 10;
  left: 0;
  width: 100%;
  max-height: 50vh; /* Adjusts dynamically based on viewport */
  // min-height: 50px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  padding: 0;
  margin: 0;
  list-style: none;
  overflow-y: auto;
  z-index: 10;
  &.top-full {
    top: 100%;
  }
  &.bottom-full {
    bottom: 100%;
  }
`;

const OptionItem = styled.li`
  padding: 7px 10px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
  background: ${(props) => (props.highlighted ? "#e0e0e0" : "transparent")};
  color: ${props => props.active ? '#192d6e' : '#000'};
  &:hover {
    background: ${props => props.active ? '#192d6e1C' : '#f0f0f0'};
  }
`;

const SelectDropdown = ({ 
  options, defaultSelection, onSelect, width, searchenabled= false, 
  styles, listplacement, placeholder, displayKey, searchKey
}) => {
  const [searchText, setSearchText] = useState("");
  const [selectedOption, setSelectedOption] = useState();
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const [typing, setTyping] = useState(false);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setHighlightIndex(-1); // Reset highlight index when opening
      if (triggerRef.current) {
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const dropdownHeight = dropdownRef.current?.offsetHeight || 0;
        const spaceBelow = window.innerHeight - triggerRect.bottom;
        setPosition(spaceBelow >= dropdownHeight ? "bottom" : "top");
      }
    }
  }, [isOpen]);

  useEffect(()=> {
    if(defaultSelection) {
      setSelectedOption(options[0])
    }
  }, [defaultSelection])

  const handleSelect = (option) => {
    setSelectedOption(option);
    setTyping(false)
    if (searchenabled) {
        setSearchText(option); // Keep selected option in input
      } else {
        setSearchText(""); // Clear text if search is disabled
      }
    setIsOpen(false);
    setClicked(false);
    onSelect(option);
  };

  const filteredOptions = !typing ? options : options?.filter((option) =>{
    return searchKey ? option[displayKey].toLowerCase().includes(searchText.toLowerCase()) : option.toLowerCase().includes(searchText.toLowerCase())
  });

  const handleKeyDown = (e) => {
    setTyping(true);
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        setIsOpen(true);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      setHighlightIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : 0 // Loop to first item
      );
    }

    if (e.key === "ArrowUp") {
      setHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : filteredOptions.length - 1 // Loop to last item
      );
    }

    if (e.key === "Enter" && highlightIndex >= 0) {
      handleSelect(filteredOptions[highlightIndex]);
    }

    if (e.key === "Escape") {
      setIsOpen(false);
      setClicked(false);
    }
  };
  const getDisplayValue = (option) => (typeof option === "object" ? option[displayKey] : option);
  const [position, setPosition] = useState("top");
  const [clicked, setClicked]= useState(false);


  useEffect(()=> {
    if(clicked) {
      setSearchText("");
    }
  }, [clicked])

  return (
    <DropdownContainer ref={triggerRef}
    className="dropdown-trigger" margin={styles.margin} width={width}>
      <DropdownInput
        type="text"
        value={searchenabled && clicked ? searchText : getDisplayValue(selectedOption)}
        onChange={(e) => searchenabled && setSearchText(e.target.value)}
        onFocus={() => {setClicked(true);setIsOpen(true)}}
        placeholder=" "
        readOnly={!searchenabled}
        onKeyDown={handleKeyDown}
      />
      <FloatingLabel>{placeholder || 'Select an option'}</FloatingLabel>

      {/* Down Arrow Icon */}
      <DownArrowIcon
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        onClick={() => setIsOpen(!isOpen)}
      >
        <polyline points="6 9 12 15 18 9"></polyline>
      </DownArrowIcon>
      {searchenabled && <SearchIcon src={'/images/search-icon.svg'}/>
      }

      {isOpen && (
        <><Backdrop onClick={()=> {setClicked(false);setIsOpen(false)}}></Backdrop>
        <OptionsList
        ref={dropdownRef}
        className={` ${position === "bottom" ? "top-full" : "bottom-full"}`}>
            {filteredOptions.map((option, index) => (
              <OptionItem
                key={index}
                active={getDisplayValue(option) === getDisplayValue(selectedOption)}
                highlighted={index === highlightIndex}
                onMouseDown={() => handleSelect(option)}
                onMouseEnter={() => setHighlightIndex(index)}
              >
                {getDisplayValue(option)}
              </OptionItem>
            ))}
          </OptionsList></>
      )}
    </DropdownContainer>
  );
};

export default SelectDropdown;
